import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/homeData';

const categoryIcons: Record<string, string> = {
  'cat-shoes-01': 'ri-footprint-line',
  'cat-hoodies-02': 'ri-t-shirt-line',
  'cat-tshirts-03': 'ri-shirt-line',
  'cat-jackets-04': 'ri-t-shirt-2-line',
  'cat-pants-05': 'ri-check-double-line',
  'cat-headwear-06': 'ri-base-station-line',
  'cat-sets-07': 'ri-stack-line',
  'cat-underwear-08': 'ri-checkbox-blank-line',
  'cat-jersey-09': 'ri-t-shirt-2-line',
  'cat-accessories-10': 'ri-vip-diamond-line',
  'cat-bags-11': 'ri-handbag-line',
  'cat-belts-12': 'ri-external-link-line',
  'cat-perfumes-13': 'ri-drop-line',
  'cat-other-14': 'ri-more-line',
};

export default function Navbar() {
  const [isDesktopDropdown, setIsDesktopDropdown] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDesktopDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-xl border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-2 text-foreground-900 no-underline">
            <span className="font-heading text-xl md:text-2xl font-bold tracking-tight whitespace-nowrap">
              Spreadsheet<span className="text-primary-500">Hub</span>
            </span>
          </Link>

          {/* Center: Desktop nav */}
          <div className="hidden md:flex items-center gap-6">
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDesktopDropdown(!isDesktopDropdown)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-background-300/60 bg-background-50/80 backdrop-blur-sm text-sm font-medium text-foreground-800 hover:border-primary-300 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <span>Categories</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDesktopDropdown ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              {isDesktopDropdown && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-72 bg-background-50 rounded-2xl border border-background-200/70 shadow-lg backdrop-blur-xl overflow-hidden z-50">
                  <div className="py-2 max-h-80 overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.seq}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-5 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors duration-150 cursor-pointer"
                        onClick={() => setIsDesktopDropdown(false)}
                      >
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className={`${categoryIcons[cat.seq]} text-foreground-400 text-sm`}></i>
                        </div>
                        <span className="whitespace-nowrap">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/kakobuy-spreadsheet-guide/" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors duration-200 whitespace-nowrap cursor-pointer">
              Kakobuy
            </Link>
            <Link to="/cssbuy-spreadsheet-guide/" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors duration-200 whitespace-nowrap cursor-pointer">
              CSSBuy
            </Link>
            <Link to="/superbuy-spreadsheet-guide/" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors duration-200 whitespace-nowrap cursor-pointer">
              Superbuy
            </Link>
          </div>

          {/* Right: Desktop CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-5 py-2.5 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Hipobuy Sheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-5 py-2.5 rounded-full border-2 border-foreground-900 text-foreground-900 text-sm font-semibold hover:bg-foreground-900 hover:text-background-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile: hamburger + CTA */}
          <div className="flex md:hidden items-center gap-2.5">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-4 py-2 rounded-full bg-foreground-900 text-background-50 text-xs font-semibold hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy Now
            </a>
            <button
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-300/60 bg-background-50/80 cursor-pointer"
            >
              <i className={`text-lg ${isMobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile fullscreen panel */}
      <div
        className={`md:hidden fixed inset-0 top-16 z-40 bg-background-50 transition-transform duration-300 ease-in-out overflow-y-auto ${
          isMobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="px-6 py-6">
          {/* Primary CTAs */}
          <div className="flex flex-col gap-3 mb-8">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setIsMobileOpen(false)}
              className="flex items-center justify-center gap-2 w-full py-3 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-all duration-200 cursor-pointer"
            >
              Hipobuy Sheet
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-sm"></i>
              </div>
            </a>
          </div>

          {/* Category section title */}
          <div className="mb-4">
            <span className="text-xs font-semibold uppercase tracking-widest text-foreground-400 font-label">
              Product Categories
            </span>
          </div>

          {/* Categories grid */}
          <div className="grid grid-cols-2 gap-3 mb-8">
            {categories.map((cat) => (
              <a
                key={cat.seq}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setIsMobileOpen(false)}
                className="flex items-center gap-3 p-3 rounded-xl border border-background-200/70 bg-background-50 hover:border-primary-200 hover:bg-background-100 transition-all duration-200 cursor-pointer"
              >
                <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                  <i className={`${categoryIcons[cat.seq]} text-foreground-600 text-sm`}></i>
                </div>
                <span className="text-sm font-medium text-foreground-800 leading-tight">{cat.name}</span>
              </a>
            ))}
          </div>

          {/* Nav links */}
          <div className="border-t border-background-200/50 pt-6">
            <span className="text-xs font-semibold uppercase tracking-widest text-foreground-400 font-label">
              Platform Guides
            </span>
            <div className="mt-4 space-y-1">
              {[
                { label: 'Hipobuy Spreadsheet Guide', to: '/best-hipobuy-spreadsheet-guide-2026/' },
                { label: 'Kakobuy Spreadsheet Guide', to: '/kakobuy-spreadsheet-guide/' },
                { label: 'CSSBuy Spreadsheet Guide', to: '/cssbuy-spreadsheet-guide/' },
                { label: 'Superbuy Spreadsheet Guide', to: '/superbuy-spreadsheet-guide/' },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsMobileOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-900 transition-colors duration-150"
                >
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-article-line text-foreground-400 text-sm"></i>
                  </div>
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Safety / About */}
          <div className="border-t border-background-200/50 pt-6 mt-6">
            <div className="space-y-1">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setIsMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-500 hover:bg-background-100 hover:text-foreground-700 transition-colors duration-150 cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-foreground-400 text-sm"></i>
                </div>
                Safety Center
              </a>
              <Link
                to="/best-hipobuy-spreadsheet-guide-2026/"
                onClick={() => setIsMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-500 hover:bg-background-100 hover:text-foreground-700 transition-colors duration-150"
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-foreground-400 text-sm"></i>
                </div>
                About SpreadsheetHub
              </Link>
            </div>
          </div>

          {/* Disclaimer */}
          <p className="mt-8 text-xs text-foreground-300 text-center leading-relaxed">
            Independent research platform.<br />Not affiliated with any shopping agent.
          </p>
        </div>
      </div>
    </nav>
  );
}